import * as React from 'react';
import { Redirect, Link } from "react-router-dom";
import * as _ from 'lodash';

import Tree from '../tree/Tree';
import EventTable from '../eventtable/EventTable';
import Statistics from '../statistics/Statistics';
import Overview from '../overview/Overview';
import Scenarios from '../scenarios/Scenarios';
import WorkEstimate from '../workestimate/WorkEstimate';
import WorkEstimateOverview from '../workestimateoverview/WorkEstimateOverview';
import TreeNode from '../../models/TreeNode';
import Event from '../../models/project/Event';
import Scenario from '../../models/Scenario';
import TreeCreator from '../../services/tree/TreeCreator';
import StatisticsCalculator from '../../services/statistics/StatisticsCalculator';
import { Logger, RecordTypes } from '../../stores/logger/Logger';

import '../../../node_modules/bootstrap/dist/css/bootstrap.css';
import '../../../node_modules/jquery/dist/jquery.js';
import '../../../node_modules/bootstrap/dist/js/bootstrap.js';
import { SuccessCriteria } from '../../models/project/SuccessCriteria';
import ProjectCriteriaParser from '../../services/project/ProjectCriteriaParser';

import './Project.css';
import ErrorHandler from '../../services/errorhandler/ErrorHandler';
import { ProjectStore } from '../../stores/project/ProjectStore';

class Project extends React.Component<{
  match: { params: any, isExact: boolean, path: string, url: string },
  location: Location,
  logger: Logger,
  baseUrl: string
}, {
    isLoaded: boolean,
    title: string,
    branches: TreeNode[],
    components: Event[],
    error: boolean,
    errorMessage: string,
    scenarios: Scenario[],
    successCriteria?: SuccessCriteria,
    tree: TreeNode[],
    headerImg?: string,
    projectStore: ProjectStore
  }> {
  constructor(props) {
    super(props);

    this.state = {
      isLoaded: false,
      title: "",
      branches: [],
      components: [],
      error: false,
      errorMessage: "",
      scenarios: [],
      tree: [],
      projectStore: new ProjectStore()
    };
  }

  // "componentDidUpdate() is invoked immediately after updating occurs. This method is not called for the initial render."
  // "This is also a good place to do network requests as long as you compare the current props to previous props (e.g. a network request may not be necessary if the props have not changed)."
  componentDidUpdate(prevProps) {
    const locationChanged = this.props.location !== prevProps.location;

    if (locationChanged) {
      this.setState({
        isLoaded: false
      });

      this.updateProjectData();
    }
  }

  // The componentDidMount() method runs after the component output has been rendered to the DOM.
  componentDidMount() {
    this.updateProjectData();
  }

  private updateProjectData() {
    const id = this.props.match.params.id;
    document.title = document.title + ": " + id;

    fetch(this.props.baseUrl + "/api/v1/project/" + id + ".json")
      .then(res => res.json())
      .then(
        (result) => {
          // need to try-catch inside async block
          // @todo create service for try-catching and logging + component for showing errors
          try {
            const title = result.title;

            const components = TreeCreator.mapComponents(result.components);
            const branches = TreeCreator.mapBranches(result.leaves, components);
            const tree = TreeCreator.flatDataToHierarchy(branches);
            const scenarios = StatisticsCalculator.calculateScenarioStatistics(tree);
            const successCriteria = ProjectCriteriaParser.mapSuccessCriteria(result.success_criteria);

            if (_.isUndefined(successCriteria)) {
              //this.props.logger.addAlert("Define project success criteria to calculate project success probability.");
            }

            if (!_.isUndefined(result.image)) {
              this.setState({
                headerImg: this.props.baseUrl + result.image
              });
            } else {
              this.setState({
                headerImg: this.props.baseUrl + "/img/default-project-header.jpg"
              });
            }

            this.setState({
              isLoaded: true,
              title: title,
              components: components,
              branches: branches,
              scenarios: scenarios,
              tree: tree,
              successCriteria: successCriteria
            });

            this.state.projectStore.notifySubscribers();
          } catch (error) {
            ErrorHandler.handleError(error, this.props.logger);
          }
        },
        (error) => {
          this.setState({
            isLoaded: true,
            error: true,
            errorMessage: error.message
          });
        }
      )
  }

  public render() {
    const windowWidth = (window.innerWidth - 50);
    const windowHeight = 500;
    const isLoaded = this.state.isLoaded;
    const isWorkEstimateSane = StatisticsCalculator.isWorkEstimateSane(this.state.scenarios);

    if (this.state.error) return <Redirect to="/404" />;

    const headerStyle = {
      backgroundImage: "url(" + this.state.headerImg + ")",
      backgroundSize: "cover"
    };

    return (
      <React.Fragment>
        <div className="row">
          <div className="col">
            <header style={headerStyle}>
              <div className="darkening">
                <div id="project-header">
                  <h1>{this.state.title}</h1>
                </div>
              </div>
            </header>
          </div>
        </div>

        <div>
          <div className="row">
            <div className="col">
              <div className="boxed" id="project-navigation">
                <nav className="nav nav-pills">
                  {this.props.location.hash === '#overview' ? (
                    <a className="nav-item nav-link active" href="#overview">Overview</a>
                  ) : (
                      <a className="nav-item nav-link" href="#overview">Overview</a>
                    )}
                  {this.state.successCriteria &&
                    (this.props.location.hash === '#statistics' ? (
                      <a className="nav-item nav-link active" href="#statistics">Statistics</a>
                    ) : (
                        <a className="nav-item nav-link" href="#statistics">Statistics</a>
                      ))
                  }
                  {isWorkEstimateSane &&
                    (this.props.location.hash === '#workestimate' ? (
                      <a className="nav-item nav-link active" href="#workestimate">Work estimate</a>
                    ) : (
                        <a className="nav-item nav-link" href="#workestimate">Work estimate</a>
                      ))
                  }
                  {this.props.location.hash === '#scenarios' ? (
                    <a className="nav-item nav-link active" href="#scenarios">Scenarios</a>
                  ) : (
                      <a className="nav-item nav-link" href="#scenarios">Scenarios</a>
                    )}
                  {this.props.location.hash === '#events' ? (
                    <a className="nav-item nav-link active" href="#events">Events</a>
                  ) : (
                      <a className="nav-item nav-link" href="#events">Events</a>
                    )}
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {isLoaded && (
            <React.Fragment>
              <div className="col-md">
                <div className="boxed" id="overview">
                  <Overview scenarios={this.state.scenarios} successCriteria={this.state.successCriteria} />
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
        {this.state.scenarios.length > 0 && isWorkEstimateSane && (
          <div className="row">
            <div className="col-md" id="workestimate">
              <WorkEstimate scenarios={this.state.scenarios} width={windowWidth} height={windowHeight} projectStore={this.state.projectStore} />
            </div>
          </div>
        )}
        {this.state.scenarios.length > 0 && !_.isUndefined(this.state.successCriteria) && (
          <div className="row">
            <div className="col-md">
              <Statistics scenarios={this.state.scenarios} width={windowWidth} height={windowHeight} successCriteria={this.state.successCriteria} projectStore={this.state.projectStore} />
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-md">
            <div className="boxed" id="scenarios">
              <Scenarios scenarios={this.state.scenarios} successCriteria={this.state.successCriteria} />

              <h3>Scenario tree</h3>
              <Tree tree={this.state.tree} width={windowWidth} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md">
            <div className="boxed" id="events">
              <EventTable events={this.state.branches} />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default Project;


// WEBPACK FOOTER //
// ./src/components/project/Project.tsx